import _ from 'lodash';
import moment from 'moment';

import Hotel from 'arbitrip-common/client/entities/Hotel';
import { getRoomData } from 'arbitrip-common/client/utils/RoomData';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';

import Currencies from '../../../../utils/Currencies';
import roomPlaceholder from '@/img/hotel_page/room_placeholder.png';

/**
 * THE ONE DATA ADAPTER BEHIND THE DEV "TABLE VIEW" AND "CARDS VIEW" PANELS.
 *
 * Pure functions, no React. Both representations read cards, visible deals, titles, room details and
 * price lines from here so they cannot disagree on what any of those is. The helpers that
 * `RoomGroups.react.js` used to define privately (`BOARD_PRECEDENCE`, `isNonRefundable`,
 * `refundableTill`, `formatPrice`, `roomDataFor`) now live here and are imported back there — one
 * implementation, no behaviour change on that panel.
 */

export const PRICE_PRECISION = 0;

/**
 * Board vocabulary and its precedence, mirroring `DealComponent.getAmenities()`. First match wins,
 * so a deal carries exactly one board label; no match means the rate sells the room alone.
 */
export const BOARD_PRECEDENCE = [
    ['board_bases.all_inclusive', 'CateringAllInclusive'],
    ['board_bases.full_board', 'CateringFullBoard'],
    ['board_bases.half_board', 'CateringHalfBoard'],
    ['breakfast', 'BreakfastIncluded'],
];
export const KEY_ROOM_ONLY = 'RoomGroupsRoomOnly';

// Plain English for the dev panels (no new i18n keys); the strings match the `en` translations of
// the same keys so the two dev panels and `RoomGroups` read alike.
const BOARD_LABELS = {
    CateringAllInclusive: 'All Inclusive',
    CateringFullBoard: 'Full Board',
    CateringHalfBoard: 'Half Board',
    BreakfastIncluded: 'Breakfast Included',
    [KEY_ROOM_ONLY]: 'Room only',
};

export const SOURCE_CATALOG = 'RoomGroupsSourceCatalog';
export const SOURCE_GROUP = 'RoomGroupsSourceGroup';

export const CANCELLATION_KEY_NON_REFUNDABLE = 'nr';
const CANCELLATION_KEY_FREE_PREFIX = 'fc:';
const OPTION_KEY_SEPARATOR = '|';

export const LABEL_NON_REFUNDABLE = 'Non-refundable';
export const LABEL_FREE_CANCELLATION = 'Free cancellation';
export const LABEL_BEFORE = 'before';
export const LABEL_NOT_DEFINED = 'not defined';
export const LABEL_TAX_INCLUDED = 'Tax included';
export const LABEL_AVG_ROOM_NIGHT = 'Avg Room/Night';
export const LABEL_IN_POLICY = 'In policy';
export const LABEL_OUT_OF_POLICY = 'Out of policy';
const LABEL_SLEEPS = 'Sleeps';
const LABEL_FOR = 'For';
const LABEL_NIGHT = 'night';
const LABEL_NIGHTS = 'nights';
const NIGHTS_SEPARATOR = ' ･ ';
const UNIT_SQUARE_METRES = 'm²';
const UNIT_SQUARE_FEET = 'ft²';
const DELTA_PREFIX = '+';
const DEADLINE_FORMAT_SHORT = 'D MMM';
const DEADLINE_FORMAT_SHORT_WITH_YEAR = 'D MMM YYYY';

export const POLICY_IN = 'in';
export const POLICY_OUT = 'out';

export const PERKS_PER_LINE = 3;
const PERK_LINES = 2;

/**
 * The card's bed surface on the wire. `packages/room-mapping-3.0/src/display/options.ts`
 * (`RoomOptionRecord`, `EMITTED_AXES_OF.bed`) emits `bed_type` + `bed_config` since the bed-axis
 * split; `bed` is what records written before that flip carry. Read in this order.
 */
const BED_AXES = ['bed_config', 'bed_type', 'bed'];
const VIEW_AXIS = 'view';
const BED_VALUE_SEPARATOR = ' · ';
export const BED_SOURCE_DETAILS = 'room details';
export const BED_SOURCE_CARD_OPTION = 'card option';
export const BED_SOURCE_CATALOG = 'room catalog';
const BED_ALTERNATIVE_SEPARATOR = ' or ';

const RE_HTML_TAG = /<[^>]*>/g;
const RE_WHITESPACE_RUN = /\s+/g;
const RE_CATALOG_SIZE = /(\d[\d,]*(?:\.\d+)?)\s*sq\.?\s*(meters|metres|feet|ft|m)\b/i;
const RE_UNIT_FEET = /^f/i;
const RE_STRONG_TEXT = /<strong>([\s\S]*?)<\/strong>/i;
const RE_BED_NOUN = /\b(?:beds?|sofa bed|bunks?|futons?)\b/i;

export function boardKeyOf(deal) {
    const hit = BOARD_PRECEDENCE.find(([path]) => _.get(deal, path));
    return hit ? hit[1] : KEY_ROOM_ONLY;
}

export function boardLabelOf(deal) {
    return BOARD_LABELS[boardKeyOf(deal)];
}

export function isRoomOnly(deal) {
    return boardKeyOf(deal) === KEY_ROOM_ONLY;
}

/**
 * `chooseprod` deals carry refundability under `dca`; `S:` deals carry a plain `refundable` flag.
 * Same split as `DealComponent.getCancellation()`.
 */
export function isNonRefundable(deal) {
    if (deal.chooseprod) {
        return Boolean(_.get(deal, 'chooseprod.dca.nonRefundable'));
    }
    return !deal.refundable;
}

export function refundableTill(deal) {
    return _.get(deal, 'chooseprod.dca.dealIsFullyRefundableTill') || deal.freeCancellationUntil || null;
}

export function cancellationKeyOf(deal) {
    if (isNonRefundable(deal)) {
        return CANCELLATION_KEY_NON_REFUNDABLE;
    }
    return `${CANCELLATION_KEY_FREE_PREFIX}${refundableTill(deal) || ''}`;
}

/**
 * The design's short deadline — `18 Sep`, or `18 Sep 2027` when the deadline's year is not the
 * current one. Scoped to the two dev views. The value is parsed exactly as
 * `DateUtils.formatCancellationDeadline` parses it for the deal row (`moment.utc(deadline)`,
 * `DealComponent.react.js:186-189`), but WITHOUT that formatter's previous-day 23:59 adjustment:
 * the deal's deadline date is shown as the date it is. `null` when there is no deadline.
 */
export function formatCancellationDeadlineShort(deal) {
    const till = refundableTill(deal);
    if (!till) {
        return null;
    }
    const deadline = moment.utc(till);
    if (!deadline.isValid()) {
        return null;
    }
    const format = deadline.year() === moment.utc().year() ? DEADLINE_FORMAT_SHORT : DEADLINE_FORMAT_SHORT_WITH_YEAR;
    return deadline.format(format);
}

export function cancellationOf(deal) {
    const nonRefundable = isNonRefundable(deal);
    const till = nonRefundable ? null : refundableTill(deal);
    return {
        key: cancellationKeyOf(deal),
        nonRefundable,
        till,
        deadline: nonRefundable ? null : formatCancellationDeadlineShort(deal),
    };
}

export function formatPrice(value, profile) {
    if (!_.isFinite(value)) {
        return '—';
    }
    return Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
        value,
        _.get(profile, 'display_currency_exchange_rate'),
        _.get(profile, 'display_currency'),
        PRICE_PRECISION,
    );
}

export function roomDataFor(hotel, deal) {
    const catalog = getRoomData(hotel, deal);
    if (catalog) {
        return { data: catalog, source: SOURCE_CATALOG };
    }
    if (deal.room_group_details) {
        return { data: deal.room_group_details, source: SOURCE_GROUP };
    }
    return null;
}

export function supplierCodeOf(deal) {
    return String(_.get(deal, 'id', '')).split(':')[0];
}

/**
 * Nights as `DealComponent.react.js:810-812` derives them (`DateUtils.parseFormatDate` on
 * `check_in` / `check_out`), falling back to `deal.nights`.
 */
export function nightsOf(deal) {
    const checkIn = DateUtils.parseFormatDate(deal.check_in);
    const checkOut = DateUtils.parseFormatDate(deal.check_out);
    if (checkIn && checkOut && checkIn.isValid() && checkOut.isValid()) {
        const nights = checkOut.diff(checkIn, 'days');
        if (_.isFinite(nights) && nights > 0) {
            return nights;
        }
    }
    return _.isFinite(deal.nights) ? deal.nights : null;
}

export function nightsLabel(nights) {
    if (!_.isFinite(nights)) {
        return null;
    }
    return `${nights} ${nights === 1 ? LABEL_NIGHT : LABEL_NIGHTS}`;
}

export function totalOf(deal) {
    const value = _.get(deal, 'totalPrice');
    return _.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function perNightOf(deal) {
    const value = _.get(deal, 'pricePerNight');
    return _.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

/** Cheapest `totalPrice` first; ties by `pricePerNight`, then by `id` so the order is stable. */
export function compareByPrice(a, b) {
    const byTotal = totalOf(a) - totalOf(b);
    if (byTotal !== 0 && !Number.isNaN(byTotal)) {
        return byTotal;
    }
    const byNight = perNightOf(a) - perNightOf(b);
    if (byNight !== 0 && !Number.isNaN(byNight)) {
        return byNight;
    }
    return String(a.id || '').localeCompare(String(b.id || ''));
}

export function optionKeyOf(boardKey, cancellationKey) {
    return `${boardKey}${OPTION_KEY_SEPARATOR}${cancellationKey}`;
}

function dealOptionKeyOf(deal) {
    return optionKeyOf(boardKeyOf(deal), cancellationKeyOf(deal));
}

export function policyOf(deal) {
    if (deal.in_policy === true) {
        return POLICY_IN;
    }
    if (deal.in_policy === false) {
        return POLICY_OUT;
    }
    return null;
}

export function priceLinesOf(deal, profile) {
    const nights = nightsOf(deal);
    const nightsText = nightsLabel(nights);
    return {
        total: formatPrice(_.get(deal, 'totalPrice'), profile),
        avg: formatPrice(_.get(deal, 'pricePerNight'), profile),
        nights,
        nightsShort: nightsText,
        nightsLine: nightsText
            ? `${LABEL_FOR} ${nightsText}${NIGHTS_SEPARATOR}${LABEL_TAX_INCLUDED}`
            : LABEL_TAX_INCLUDED,
        policy: policyOf(deal),
    };
}

/**
 * The radio price: the difference between this combination's total and the card's cheapest deal.
 * The cheapest option itself shows nothing.
 */
export function deltaLabelOf(deal, cheapest, profile) {
    if (!deal || !cheapest) {
        return null;
    }
    const delta = totalOf(deal) - totalOf(cheapest);
    if (!_.isFinite(delta) || delta <= 0) {
        return null;
    }
    return `${DELTA_PREFIX}${formatPrice(delta, profile)}`;
}

function optionValueOf(options, axes) {
    const values = axes
        .map((axis) => {
            const hit = options.find((option) => option && option.axis === axis);
            return hit ? String(hit.value || '').trim() : '';
        })
        .filter(Boolean);
    return values.length > 0 ? _.uniq(values).join(BED_VALUE_SEPARATOR) : undefined;
}

function imageUrlsOf(room, source) {
    const images = Array.isArray(room.images) ? room.images : [];
    if (source === SOURCE_CATALOG) {
        // Expedia catalog rooms pass through `ImageUtils.transformImagesToGallery` (`HotelUtils.setRooms`).
        return images.map((image) => image && image.original).filter(Boolean);
    }
    return images.map((image) => image && image.imageUrl).filter(Boolean);
}

function perksOf(room, source) {
    if (source === SOURCE_CATALOG) {
        return (Array.isArray(room.amenities) ? room.amenities : [])
            .map((amenity) => amenity && String(amenity.name || '').trim())
            .filter(Boolean);
    }
    return (Array.isArray(room.perks) ? room.perks : []).map((perk) => String(perk || '').trim()).filter(Boolean);
}

function stripHtml(text) {
    return String(text || '')
        .replace(RE_HTML_TAG, ' ')
        .replace(RE_WHITESPACE_RUN, ' ')
        .trim();
}

function isPresentNumber(value) {
    return value !== '' && value !== null && value !== undefined && _.isFinite(Number(value));
}

function bedGroupTextOf(group) {
    const description = String((group && group.description) || '').trim();
    if (description) {
        return description;
    }
    const configuration = Array.isArray(group && group.configuration) ? group.configuration : [];
    return configuration
        .map((bed) => [bed && bed.quantity, bed && bed.size, bed && bed.type].filter(Boolean).join(' '))
        .filter(Boolean)
        .join(', ');
}

function catalogBedOf(room) {
    const bedGroups = room.bed_groups;
    const groups = Array.isArray(bedGroups) ? bedGroups : Object.values(bedGroups || {});
    const alternatives = _.uniq(groups.map(bedGroupTextOf).filter(Boolean));
    if (alternatives.length > 0) {
        return alternatives.join(BED_ALTERNATIVE_SEPARATOR);
    }
    const strong = String(room.description || '').match(RE_STRONG_TEXT);
    const text = strong ? stripHtml(strong[1]) : '';
    return text && RE_BED_NOUN.test(text) ? text : undefined;
}

function catalogSizeOf(room) {
    const metres = _.get(room, 'info.room_size.metre_square');
    if (isPresentNumber(metres)) {
        return `${metres} ${UNIT_SQUARE_METRES}`;
    }
    const feet = _.get(room, 'info.room_size.square_feet');
    if (isPresentNumber(feet)) {
        return `${feet} ${UNIT_SQUARE_FEET}`;
    }
    const match = stripHtml(room.description).match(RE_CATALOG_SIZE);
    if (!match) {
        return undefined;
    }
    return `${match[1]} ${RE_UNIT_FEET.test(match[2]) ? UNIT_SQUARE_FEET : UNIT_SQUARE_METRES}`;
}

function bedOf(room, source) {
    if (source === SOURCE_CATALOG) {
        return catalogBedOf(room);
    }
    if (source !== SOURCE_GROUP) {
        return undefined;
    }
    const bedTypes = room.bedTypes;
    if (Array.isArray(bedTypes)) {
        const joined = bedTypes
            .map((bed) => String(bed || '').trim())
            .filter(Boolean)
            .join(', ');
        return joined || undefined;
    }
    const single = String(bedTypes || '').trim();
    return single || undefined;
}

function sizeOf(room, source) {
    return source === SOURCE_CATALOG ? catalogSizeOf(room) : undefined;
}

function sleepsOf(room, source) {
    if (source === SOURCE_CATALOG) {
        const total = _.get(room, 'occupancy.max_allowed.total');
        return isPresentNumber(total) ? `${LABEL_SLEEPS} ${total}` : undefined;
    }
    if (source !== SOURCE_GROUP) {
        return undefined;
    }
    const occupancy = room.roomMaxOccupancy;
    return _.isFinite(Number(occupancy)) && occupancy !== '' && occupancy !== null
        ? `${LABEL_SLEEPS} ${occupancy}`
        : undefined;
}

function aboutOf(room, source) {
    const text = source === SOURCE_CATALOG ? room.description : room.roomDescriptionParagraph;
    const trimmed = String(text || '').trim();
    return trimmed || null;
}

const NO_DETAILS = Object.freeze({
    hasDetails: false,
    room: null,
    source: null,
    deal: null,
    name: null,
    image: roomPlaceholder,
    images: [],
    imageCount: 0,
    size: undefined,
    bed: undefined,
    bedSource: null,
    sleeps: undefined,
    view: undefined,
    perks: [],
    perkLines: [],
    about: null,
});

/**
 * Room details for one card: `getRoomData` (Expedia catalog room) first, else the deal's
 * `room_group_details` (Gimmonix/group shape) — resolved on the cheapest visible deal first, then on
 * any other deal of the card. No carrier at all → `hasDetails: false`, and the views render the
 * placeholder image (no photo badge), no chips and no perk lines for that card.
 */
export function resolveDetails(hotel, deals, cardOptions) {
    let carrier = null;
    for (const deal of deals) {
        const resolved = roomDataFor(hotel, deal);
        if (resolved) {
            carrier = { ...resolved, deal };
            break;
        }
    }
    if (!carrier) {
        return NO_DETAILS;
    }

    const { data: room, source, deal } = carrier;
    const images = imageUrlsOf(room, source);
    const perks = perksOf(room, source);
    const bedFromDetails = bedOf(room, source);
    const bedFromCard = optionValueOf(cardOptions, BED_AXES);
    const bed = bedFromDetails || bedFromCard;
    let bedSource = null;
    if (bedFromDetails) {
        bedSource = source === SOURCE_CATALOG ? BED_SOURCE_CATALOG : BED_SOURCE_DETAILS;
    } else if (bedFromCard) {
        bedSource = BED_SOURCE_CARD_OPTION;
    }

    return {
        hasDetails: true,
        room,
        source,
        deal,
        name: String(room.name || '').trim() || null,
        image: images[0] || roomPlaceholder,
        images,
        imageCount: images.length,
        size: sizeOf(room, source),
        bed,
        bedSource,
        sleeps: sleepsOf(room, source),
        view: optionValueOf(cardOptions, [VIEW_AXIS]),
        perks,
        perkLines: _.chunk(perks, PERKS_PER_LINE).slice(0, PERK_LINES),
        about: aboutOf(room, source),
    };
}

function cancellationOptionsOf(visible) {
    const byKey = _.groupBy(visible, cancellationKeyOf);
    return Object.values(byKey)
        .map((sameKey) => {
            const cheapest = sameKey[0];
            return { ...cancellationOf(cheapest), cheapestTotal: totalOf(cheapest) };
        })
        .sort((a, b) => a.cheapestTotal - b.cheapestTotal);
}

function boardOptionsOf(visible) {
    const byKey = _.groupBy(visible, boardKeyOf);
    return Object.values(byKey)
        .map((sameKey) => {
            const cheapest = sameKey[0];
            return {
                key: boardKeyOf(cheapest),
                label: boardLabelOf(cheapest),
                roomOnly: isRoomOnly(cheapest),
                cheapestTotal: totalOf(cheapest),
            };
        })
        .sort((a, b) => a.cheapestTotal - b.cheapestTotal);
}

/**
 * The page's own dominance rule, on CARD scope. `markSupersededDeals` only ever compares deals that
 * carry `chooseprod` (`Hotel.js:846`) and `isDeal1BetterThanDeal2` reads `chooseprod.dca` and
 * `board_bases` unguarded, so a deal missing either is never compared — and never hidden.
 */
function isComparable(deal) {
    return Boolean(deal && deal.id && deal.board_bases && _.get(deal, 'chooseprod.dca'));
}

/**
 * `compareSuperseded` (`Hotel.js:119-138`) with `card_id` in place of `room_group`: `deal` is
 * superseded by `other` iff `other` is better and `deal` is not; when both or neither is better the
 * page's id arbiter (`deal1.id.substring(2) < deal2.id.substring(2)`) keeps exactly one of the pair.
 */
function supersededBy(deal, other) {
    if (deal === other || !isComparable(deal) || !isComparable(other)) {
        return false;
    }
    const dealBetter = Hotel.isDeal1BetterThanDeal2(deal, other);
    const otherBetter = Hotel.isDeal1BetterThanDeal2(other, deal);
    if (dealBetter && otherBetter) {
        return false;
    }
    if (dealBetter !== otherBetter) {
        return otherBetter;
    }
    return String(deal.id).substring(2) < String(other.id).substring(2);
}

function visibleDealsOf(sorted) {
    return sorted.filter((deal) => !sorted.some((other) => supersededBy(deal, other)));
}

function firstNonEmpty(deals, path) {
    for (const deal of deals) {
        const value = String(_.get(deal, path) || '').trim();
        if (value) {
            return value;
        }
    }
    return '';
}

/**
 * Build the cards both views render.
 *
 * (a) A card is `room_display_group.card_id`; deals without one are counted, not shown.
 * (b) Within a card a deal is hidden iff another deal of the card dominates it per the page's own
 *     `isDeal1BetterThanDeal2` / `compareSuperseded` (id arbiter on ties) — superseded at CARD level
 *     (`deal.superseded` is never set on room-mapped hotels, and its key is the fine `room_group`,
 *     which would leave duplicates when two fine groups share one card). `board | cancellationKey`
 *     is only the OPTION ADDRESS for the Cards view radios; two non-comparable visible deals on one
 *     address resolve to the cheaper one and are counted in `alternativesKept`, not hidden.
 * (c) Visible deals cheapest-first; cards by their cheapest deal, then title, then `card_id`.
 * (d) Title = group label when the display group has one card or the card has no sublabel;
 *     otherwise the sublabel (`room_option_description`, the supplier's verbatim rate-line string,
 *     RO-6 — read off the cheapest visible deal), with the group label as a smaller line under it.
 */
export function buildDealViews(deals, hotel) {
    const list = Array.isArray(deals) ? deals : [];
    const withCard = list.filter((deal) => _.get(deal, 'room_display_group.card_id'));
    const withoutCardCount = list.length - withCard.length;

    const byCard = _.groupBy(withCard, (deal) => deal.room_display_group.card_id);
    const cardsPerGroup = _.countBy(Object.values(byCard), (cardDeals) =>
        _.get(cardDeals[0], 'room_display_group.key'),
    );

    const cards = Object.entries(byCard).map(([cardId, cardDeals]) => {
        const sorted = [...cardDeals].sort(compareByPrice);
        const visible = visibleDealsOf(sorted);
        const cheapest = visible[0];
        const groupKey = _.get(cheapest, 'room_display_group.key');
        const groupLabel = firstNonEmpty(visible, 'room_display_group.label');
        const sublabel = firstNonEmpty(visible, 'room_option_description');
        const cardsInGroup = cardsPerGroup[groupKey] || 1;
        const usesSublabel = cardsInGroup > 1 && Boolean(sublabel);
        const fallbackTitle = String(cheapest.description || '').trim();
        let title = usesSublabel ? sublabel : groupLabel;
        if (!title) {
            title = fallbackTitle;
        }
        const cardOptions =
            _.has(cheapest, 'room_options') && Array.isArray(cheapest.room_options) ? cheapest.room_options : [];
        // `visible` is price-sorted, so the first deal seen on an address is the cheaper one.
        const dealByOption = {};
        visible.forEach((deal) => {
            const address = dealOptionKeyOf(deal);
            if (!dealByOption[address]) {
                dealByOption[address] = deal;
            }
        });
        const alternativesKept = visible.length - Object.keys(dealByOption).length;

        return {
            cardId,
            groupKey,
            groupLabel,
            sublabel,
            cardsInGroup,
            title,
            subtitleLine: usesSublabel && groupLabel && groupLabel !== sublabel ? groupLabel : null,
            deals: visible,
            allDeals: sorted,
            supersededCount: sorted.length - visible.length,
            alternativesKept,
            cheapest,
            options: cardOptions,
            details: resolveDetails(hotel, visible, cardOptions),
            cancellationOptions: cancellationOptionsOf(visible),
            boardOptions: boardOptionsOf(visible),
            dealByOption,
        };
    });

    cards.sort((a, b) => {
        const byPrice = compareByPrice(a.cheapest, b.cheapest);
        if (byPrice !== 0) {
            return byPrice;
        }
        const byTitle = a.title.localeCompare(b.title);
        if (byTitle !== 0) {
            return byTitle;
        }
        return a.cardId.localeCompare(b.cardId);
    });

    return { cards, withoutCardCount };
}

export function dealForOption(card, boardKey, cancellationKey) {
    return card.dealByOption[optionKeyOf(boardKey, cancellationKey)] || null;
}

export function defaultSelectionOf(card) {
    return { boardKey: boardKeyOf(card.cheapest), cancellationKey: cancellationKeyOf(card.cheapest) };
}
